export type TrendDirection = 'up' | 'down' | 'flat' | 'empty'

export type RevenueTrendSummary = {
  direction: TrendDirection
  /** Second-half vs first-half change ratio (null when first half is 0). */
  changePct: number | null
  firstHalf: number
  secondHalf: number
}

/** Compare Jul-Dec to Jan-Jun to judge growth vs decline across the year. */
export function summarizeRevenueTrend(amounts: number[]): RevenueTrendSummary {
  const values = amounts.slice(0, 12)
  while (values.length < 12) values.push(0)
  const firstHalf = values.slice(0, 6).reduce((sum, n) => sum + n, 0)
  const secondHalf = values.slice(6, 12).reduce((sum, n) => sum + n, 0)
  if (firstHalf === 0 && secondHalf === 0) {
    return { direction: 'empty', changePct: null, firstHalf, secondHalf }
  }
  if (firstHalf === 0) {
    return { direction: 'up', changePct: null, firstHalf, secondHalf }
  }
  const changePct = (secondHalf - firstHalf) / firstHalf
  const direction: TrendDirection =
    Math.abs(changePct) < 0.02 ? 'flat' : changePct > 0 ? 'up' : 'down'
  return { direction, changePct, firstHalf, secondHalf }
}

/** Full green at this H2-vs-H1 gain (+100%). */
export const TREND_GREEN_FULL_AT = 1
/** Full red at this H2-vs-H1 drop (-90%). */
export const TREND_RED_FULL_AT = 0.9
/** Weakest mix % when there is any non-zero change. */
const TREND_MIX_MIN_PCT = 22
/** Strongest mix % at full intensity. */
const TREND_MIX_MAX_PCT = 100

/**
 * Continuous stroke/fill colour from H1→H2 change.
 * Near 0% → muted; larger gains → deeper green; larger drops → deeper red.
 * ``changePct === null`` (H1 was zero, H2 positive) → full green.
 */
export function revenueTrendToneColor(changePct: number | null): string {
  if (changePct == null) {
    return `color-mix(in srgb, var(--colour-success) ${TREND_MIX_MAX_PCT}%, var(--colour-text-muted))`
  }
  if (!Number.isFinite(changePct) || changePct === 0) {
    return 'var(--colour-text-muted)'
  }
  const strength =
    changePct > 0
      ? Math.min(changePct / TREND_GREEN_FULL_AT, 1)
      : Math.min(-changePct / TREND_RED_FULL_AT, 1)
  const mixPct = Math.round(TREND_MIX_MIN_PCT + strength * (TREND_MIX_MAX_PCT - TREND_MIX_MIN_PCT))
  const colour = changePct > 0 ? 'var(--colour-success)' : 'var(--colour-danger)'
  return `color-mix(in srgb, ${colour} ${mixPct}%, var(--colour-text-muted))`
}
